import { PredefinedStateName } from 'src/core/constants/predefined-state-name.constant';
import { StateDefinitionDto } from 'src/core/models/state-definition/state-definition.dto';
import { StateType } from 'src/core/models/state-definition/state-type.enum';

// ─── Tone ──────────────────────────────────────────────────────────────────

export type StatusTone =
  | 'offline'
  | 'available'
  | 'oncall'
  | 'acw'
  | 'bcw'
  | 'training'
  | 'meeting'
  | 'break';

/**
 * Available, OnCall, ACW and BCW are all StateType.Work but need distinct tones.
 * All other states resolve directly from their backend StateType.
 */
const SPECIAL_NAME_TONES: Partial<Record<string, StatusTone>> = {
  [PredefinedStateName.Available]: 'available',
  [PredefinedStateName.OnCall]: 'oncall',
  [PredefinedStateName.Acw]: 'acw',
  [PredefinedStateName.Bcw]: 'bcw',
};

const STATE_TYPE_TONES: Record<StateType, StatusTone> = {
  [StateType.Offline]: 'offline',
  [StateType.Work]: 'training',
  [StateType.Break]: 'break',
  [StateType.Training]: 'training',
  [StateType.Meeting]: 'meeting',
  [StateType.Other]: 'offline',
};

export function resolveStatusTone(stateName: string, stateType: StateType): StatusTone {
  return SPECIAL_NAME_TONES[stateName] ?? STATE_TYPE_TONES[stateType] ?? 'offline';
}

export function getStatusColorClass(tone: StatusTone): string {
  return `agent-status-color-${tone}`;
}

export function getStatusDotClass(tone: StatusTone): string {
  return `agent-status-bg-${tone}`;
}

// ─── Predefined state helpers ───────────────────────────────────────────────

const PREDEFINED_NAMES = new Set<string>(Object.values(PredefinedStateName));

/**
 * Fixed logical order for the core work states in the status dropdown: Offline, Available,
 * On Call, ACW and BCW must always appear in this sequence regardless of their backend id
 * (BCW was added later and therefore has the highest id of the set).
 */
const PREDEFINED_DISPLAY_ORDER: readonly string[] = [
  PredefinedStateName.Offline,
  PredefinedStateName.Available,
  PredefinedStateName.OnCall,
  PredefinedStateName.Acw,
  PredefinedStateName.Bcw,
];

/** True for system-managed states that the user cannot select manually. */
export function isAutoState(stateName: string): boolean {
  return (
    stateName === PredefinedStateName.OnCall ||
    stateName === PredefinedStateName.Acw ||
    stateName === PredefinedStateName.Bcw
  );
}

/** True when stateName matches one of the built-in predefined states. */
export function isPredefinedStateName(stateName: string): boolean {
  return PREDEFINED_NAMES.has(stateName);
}

export function isSelectableAgentStatus(stateName: string | null | undefined): boolean {
  return stateName === PredefinedStateName.Available;
}

/**
 * Orders status dropdown items so the core work states (Offline, Available, On Call, ACW, BCW)
 * always appear first in that fixed sequence; all remaining states (tenant-defined or other
 * predefined states like Training/Meeting/Break) keep their relative order by id afterwards.
 * @param a The first state definition to compare.
 * @param b The second state definition to compare.
 * @returns A negative number if `a` sorts before `b`, positive if after, zero if equivalent.
 */
export function compareStatusDisplayOrder(a: StateDefinitionDto, b: StateDefinitionDto): number {
  const aRank = PREDEFINED_DISPLAY_ORDER.indexOf(a.name);
  const bRank = PREDEFINED_DISPLAY_ORDER.indexOf(b.name);
  const aOrder = aRank === -1 ? Number.MAX_SAFE_INTEGER : aRank;
  const bOrder = bRank === -1 ? Number.MAX_SAFE_INTEGER : bRank;

  if (aOrder !== bOrder) {
    return aOrder - bOrder;
  }

  return a.id - b.id;
}

// ─── Localization ───────────────────────────────────────────────────────────

export function getStatusLocalizationKey(stateName: string): string {
  return `Settings::WorkEnvironment:StatusManagement:${stateName.replace(/\s+/g, '')}`;
}
